import { useState, useEffect } from "react";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  Zap,
  Shield,
  BarChart3,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useVmCostAnalysis } from "../../../hooks/aiHooks/useVmCostAnalysis";
import AIThinkingLoader from "../../common/AIThinkingLoader";

const AIVmCostAnalysisCard = ({ vmId, vmName }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const { getVmCostAnalysis } = useVmCostAnalysis();

  useEffect(() => {
    if (vmId) {
      fetchAnalysisData();
    }
  }, [vmId]);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getVmCostAnalysis(vmId);
      if (result.success && result.data) {
        setAnalysis(result.data);
      }
    } catch (err) {
      console.error("Error fetching VM cost analysis:", err);
      setError("Failed to load AI analysis");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getTrendBadge = (trend, change) => {
    const upperTrend = trend?.toUpperCase();
    if (upperTrend === "INCREASING") {
      return {
        text: "INCREASING",
        color: "bg-red-500/20 text-red-400 border-red-500/30",
        icon: <TrendingUp className="w-3.5 h-3.5" />,
        directionIcon: <ArrowUp className="w-3 h-3" />,
      };
    }
    if (upperTrend === "DECREASING") {
      return {
        text: "DECREASING",
        color: "bg-green-500/20 text-green-400 border-green-500/30",
        icon: <TrendingDown className="w-3.5 h-3.5" />,
        directionIcon: <ArrowDown className="w-3 h-3" />,
      };
    }
    return {
      text: "STABLE",
      color: "bg-blue-500/20 text-blue-400 border-blue-500/30",
      icon: <Minus className="w-3.5 h-3.5" />,
      directionIcon: null,
    };
  };

  const getPredictionDirectionIcon = (direction) => {
    const upperDir = direction?.toUpperCase();
    if (upperDir === "INCREASE") {
      return <ArrowUp className="w-3 h-3 text-red-400" />;
    }
    if (upperDir === "DECREASE") {
      return <ArrowDown className="w-3 h-3 text-green-400" />;
    }
    return <Minus className="w-3 h-3 text-blue-400" />;
  };

  const getConfidenceBadge = (confidence) => {
    const upperConf = confidence?.toUpperCase();
    if (upperConf === "HIGH") {
      return "bg-green-500/20 text-green-400 border-green-500/30";
    }
    if (upperConf === "MEDIUM") {
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    }
    return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  const getActionBadge = (action) => {
    const upperAction = action?.toUpperCase();
    if (upperAction === "CONSIDER_RESERVATIONS") {
      return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    }
    if (upperAction === "OPTIMIZE_VMS") {
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    }
    if (upperAction === "REVIEW_SHARED_COSTS") {
      return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    }
    return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (!vmId) {
    return null;
  }

  if (loading) {
    return <AIThinkingLoader />;
  }

  if (error || !analysis) {
    return (
      <div
        className="rounded-xl border border-primary-cyan/20 overflow-hidden mt-4 sm:mt-6 mx-2 sm:mx-0"
        style={{ backgroundColor: "#10182b" }}
      >
        <div
          className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20"
          style={{ backgroundColor: "#10182b" }}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
                <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-primary-cyan" />
              </div>
              <span className="text-primary-cyan text-xs sm:text-base font-bold tracking-wide">
                AI COST INSIGHTS
              </span>
            </div>
            <span className="text-[10px] sm:text-[11px] text-gray-500 uppercase tracking-wider font-medium">
              Last 12 months Costs
            </span>
          </div>
        </div>
        <div className="p-6 sm:p-8 text-center" style={{ backgroundColor: "#10182b" }}>
          <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-gray-500 mx-auto mb-2" />
          <p className="text-gray-500 text-xs sm:text-sm">
            No cost analysis available for this VM
          </p>
          <p className="text-gray-600 text-[10px] sm:text-xs mt-1">
            Run analysis to get insights
          </p>
        </div>
      </div>
    );
  }

  const trendBadge = getTrendBadge(
    analysis.trend,
    analysis.monthOverMonthChange,
  );
  const isPositiveChange = analysis.monthOverMonthChange > 0;

  return (
    <div
      className="rounded-xl border border-primary-cyan/20 overflow-hidden mt-4 sm:mt-6 mx-2 sm:mx-0"
      style={{ backgroundColor: "#10182b" }}
    >
      <div
        className="px-3 sm:px-5 py-3 border-b border-primary-cyan/20 sticky top-0 z-5"
        style={{ backgroundColor: "#10182b" }}
      >
        <div className="flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
              <DollarSign className="w-3 h-3 sm:w-4 sm:h-4 text-primary-cyan" />
            </div>
            <span className="text-primary-cyan text-xs sm:text-base font-bold tracking-wide">
              AI COST INSIGHTS
            </span>
            <span className="hidden sm:inline text-[10px] sm:text-[11px] text-gray-500 uppercase tracking-wider font-medium ml-2">
              Last 12 months Costs
            </span>
          </div>
          <button
            onClick={toggleExpand}
            className="flex items-center gap-1 px-2 sm:px-3 py-1 rounded-lg bg-primary-cyan/10 hover:bg-primary-cyan/20 transition-all duration-200 text-primary-cyan text-xs sm:text-sm font-medium"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Show Details</span>
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 sm:p-5" style={{ backgroundColor: "#10182b" }}>
          {analysis.summary && (
            <div
              className="mb-4 sm:mb-5 rounded-xl p-3 sm:p-4"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">
                  Summary
                </span>
              </div>
              <p className="text-gray-200 text-sm sm:text-base leading-relaxed break-words">
                {analysis.summary}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-5">
            <div
              className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">
                  Trend & Change
                </span>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] sm:text-xs font-semibold ${trendBadge.color}`}
                >
                  {trendBadge.icon}
                  <span>{trendBadge.text}</span>
                </div>
                <div className="flex items-center gap-1">
                  {isPositiveChange ? (
                    <ArrowUp className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />
                  ) : (
                    <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                  )}
                  <span
                    className={`text-base sm:text-lg font-bold ${isPositiveChange ? "text-red-400" : "text-green-400"}`}
                  >
                    {Math.abs(analysis.monthOverMonthChange).toFixed(1)}%
                  </span>
                </div>
              </div>
              {analysis.changeCause && (
                <p className="text-gray-400 text-xs sm:text-sm mt-2 break-words">
                  Cause: {analysis.changeCause}
                </p>
              )}
            </div>

            <div
              className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">
                  Monthly Average
                </span>
              </div>
              <p className="text-2xl sm:text-3xl font-extrabold text-white">
                {formatCurrency(analysis.averageMonthly)}
              </p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <div>
                  <p className="text-gray-500 text-[10px] sm:text-xs">Peak Month</p>
                  <p className="text-gray-300 text-xs sm:text-sm font-medium break-words">
                    {analysis.peakMonth || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500 text-[10px] sm:text-xs">Lowest Month</p>
                  <p className="text-gray-300 text-xs sm:text-sm font-medium break-words">
                    {analysis.lowestMonth || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-4 sm:mb-5">
            <div
              className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">
                  Prediction
                </span>
              </div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <span className="text-gray-300 text-xs sm:text-sm">Next Month:</span>
                <span className="text-white font-semibold text-xs sm:text-sm">
                  {analysis.predictedNextMonth || "N/A"}
                </span>
              </div>
              <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
                <span className="text-gray-300 text-xs sm:text-sm">Predicted Total:</span>
                <span className="text-white font-bold text-xs sm:text-sm">
                  {formatCurrency(analysis.predictedTotal)}
                </span>
              </div>
              <div className="flex flex-wrap justify-between items-center gap-2">
                <span className="text-gray-300 text-xs sm:text-sm">Change:</span>
                <div className="flex items-center gap-1">
                  {getPredictionDirectionIcon(analysis.predictedDirection)}
                  <span
                    className={`font-semibold text-xs sm:text-sm ${analysis.predictedChangePercent > 0 ? "text-red-400" : analysis.predictedChangePercent < 0 ? "text-green-400" : "text-blue-400"}`}
                  >
                    {Math.abs(analysis.predictedChangePercent || 0).toFixed(1)}%
                  </span>
                </div>
              </div>
              {analysis.predictionConfidence && (
                <div className="mt-3">
                  <span
                    className={`text-[10px] sm:text-xs px-2 py-1 rounded-full font-semibold ${getConfidenceBadge(analysis.predictionConfidence)}`}
                  >
                    Confidence: {analysis.predictionConfidence}
                  </span>
                </div>
              )}
            </div>

            <div
              className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200"
              style={{ backgroundColor: "#1c2436" }}
            >
              <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                  <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                </div>
                <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">
                  Reservation & Shared
                </span>
              </div>
              <div className="space-y-2">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <span className="text-gray-400 text-xs sm:text-sm">
                    Reservation Savings:
                  </span>
                  <span className="text-green-400 text-xs sm:text-sm font-semibold">
                    {analysis.reservationSavingsPercent?.toFixed(1)}%
                  </span>
                </div>
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <span className="text-gray-400 text-xs sm:text-sm">Shared Cost %:</span>
                  <span className="text-gray-300 text-xs sm:text-sm">
                    {analysis.sharedCostPercent?.toFixed(1)}%
                  </span>
                </div>
                {analysis.costEfficiencyAction && (
                  <div className="mt-2 pt-2 border-t border-gray-700">
                    <span
                      className={`text-[10px] sm:text-xs px-2 py-1 rounded-full font-semibold ${getActionBadge(analysis.costEfficiencyAction)}`}
                    >
                      {analysis.costEfficiencyAction?.replace(/_/g, " ")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className="rounded-xl p-3 sm:p-4 transition-all hover:scale-[1.02] duration-200"
            style={{ backgroundColor: "#1c2436" }}
          >
            <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
              </div>
              <span className="text-primary-cyan text-xs sm:text-sm font-bold uppercase tracking-wide">
                Recommendation
              </span>
            </div>
            <p className="text-gray-200 text-sm sm:text-base leading-relaxed font-medium mb-3 break-words">
              {analysis.recommendationSuggestion || "No recommendation available"}
            </p>
            {analysis.recommendationAction &&
              analysis.recommendationAction !== "NONE" && (
                <div className="mt-3">
                  <span
                    className={`text-[10px] sm:text-xs px-2 sm:px-3 py-1 rounded-full font-semibold ${getActionBadge(analysis.recommendationAction)}`}
                  >
                    Action: {analysis.recommendationAction.replace(/_/g, " ")}
                  </span>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIVmCostAnalysisCard;